import { configuredStayDates, selectedStayDates } from './listingLocalGuide.js';

const activeRequests = new WeakMap();
const MAX_WEATHER_STAY_NIGHTS = 90;
const MILLISECONDS_PER_DAY = 86400000;

function epochDay(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
    if (!match) return null;

    const [, year, month, day] = match.map(Number);
    const timestamp = Date.UTC(year, month - 1, day);
    const date = new Date(timestamp);

    return date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day
        ? timestamp / MILLISECONDS_PER_DAY
        : null;
}

export function exceedsWeatherStayLimit(stay) {
    const checkIn = epochDay(stay?.checkIn);
    const checkOut = epochDay(stay?.checkOut);

    if (checkIn === null || checkOut === null || checkOut <= checkIn) {
        return false;
    }

    return checkOut - checkIn > MAX_WEATHER_STAY_NIGHTS;
}

function hide(root) {
    activeRequests.get(root)?.abort();
    activeRequests.delete(root);
    root.replaceChildren();
    root.hidden = true;
}

async function refresh(root, fallbackStay = null) {
    const stay = configuredStayDates(root.dataset) ?? fallbackStay;
    const listingId = root.dataset.listingId;

    if (!stay || !listingId || exceedsWeatherStayLimit(stay)) {
        hide(root);
        return;
    }

    activeRequests.get(root)?.abort();
    const controller = new AbortController();
    activeRequests.set(root, controller);
    root.hidden = true;

    const query = new URLSearchParams({
        listingId,
        checkIn: stay.checkIn,
        checkOut: stay.checkOut
    });

    try {
        const response = await fetch(`/weather/listing?${query.toString()}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            signal: controller.signal
        });

        if (!response.ok)
            throw new Error(`Weather request failed: ${response.status}`);

        const payload = await response.json();
        if (activeRequests.get(root) !== controller) return;

        if (typeof payload.html !== 'string' || !payload.html.trim()) {
            hide(root);
            return;
        }

        root.innerHTML = payload.html;
        root.hidden = false;
    } catch (error) {
        if (error.name !== 'AbortError') hide(root);
    } finally {
        if (activeRequests.get(root) === controller) {
            activeRequests.delete(root);
        }
    }
}

const listingWeather = {
    init() {
        const roots = Array.from(
            document.querySelectorAll('[data-listing-weather]')
        );
        if (roots.length === 0) return;

        const render = (fallbackStay) => {
            roots.forEach((root) => refresh(root, fallbackStay));
        };

        render(selectedStayDates());

        window.addEventListener('listing-booking-dates-changed', (event) => {
            const checkIn = event.detail?.checkIn ?? '';
            const checkOut = event.detail?.checkOut ?? '';
            const fallbackStay =
                /^\d{4}-\d{2}-\d{2}$/.test(checkIn) &&
                /^\d{4}-\d{2}-\d{2}$/.test(checkOut)
                    ? { checkIn, checkOut }
                    : null;

            render(fallbackStay);
        });
    }
};

export default listingWeather;
